import Div from 'components/Div';
import { getOrderContext } from 'contexts/order';
import { getReferenceContext } from 'contexts/reference';
import React from 'react';
import { NotificationManager } from 'react-notifications';
import { createSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Req from 'utils/Request';
import * as Types from './types';

const getStoreName = (address) => address?.store ?? address?.storeName;

const getSectionData = (response) => response?.data?.data ?? {};

const getComponentKey = (tab) => {
  if (tab === 'Pizza' || tab === 'Combo') return tab;
  if ((tab === 'CC' || tab === 'CC (Pizza)')) return 'Pizza';
  return 'Others';
};

const Foodcart = () => {
  const navigate = useNavigate();
  const [menuData, setMenuData] = React.useState({});
  const [comboData, setComboData] = React.useState({});
  const { setIsLoading } = React.useContext(getReferenceContext);
  const [selectedTab, setSelectedTab] = React.useState('');
  const { address, custPhone } = React.useContext(getOrderContext);

  const tabs = React.useMemo(
    () => {
      const menuTabs = Object.keys(menuData)
        .filter((type) => type !== 'Emp.Meal')
        .map((type) => ({ key: `menu:${type}`, type, source: 'menu' }));

      if (!Object.keys(comboData).length) return menuTabs;

      return [...menuTabs, { key: 'combo:Combo', type: 'Combo', source: 'combo' }];
    },
    [menuData, comboData]
  );

  const fetchData = React.useCallback(async () => {
    const storeName = getStoreName(address);
    if (!storeName) return;

    try {
      setIsLoading(true);
      const [menuResult, comboResult] = await Promise.all([
        Req({ url: '/menu2/menu/getMenusForOrder', body: { storeName, date: address.date ? address.date : undefined, orderType: address.haveDelivery ? 'delivery' : 'pickup' }, hideSuccess: true }),
        Req({ url: '/menu2/combo/getCombosForOrder', body: { storeName, date: address.date ? address.date : undefined }, hideSuccess: true }),
      ]);

      const mergedData = {
        menu: getSectionData(menuResult),
        combo: getSectionData(comboResult),
      };
      const nextTabs = [
        ...Object.keys(mergedData.menu)
          .filter((type) => type !== 'Emp.Meal')
          .map((type) => `menu:${type}`),
        ...(Object.keys(mergedData.combo).length ? ['combo:Combo'] : []),
      ];
      if (!nextTabs.length) throw new Error('Menu data is empty');

      setMenuData(mergedData.menu);
      setComboData(mergedData.combo);
      setSelectedTab((current) => (current && nextTabs.includes(current) ? current : nextTabs[0]));
    } catch (e) {
      NotificationManager.error(e.message, 'Error getMenus', 10000);
    } finally {
      setIsLoading(false);
    }
  }, [address, setIsLoading]);

  React.useEffect(() => {
    if (address) fetchData();
  }, [address, fetchData]);

  React.useEffect(() => {
    if (!address && custPhone) {
      navigate({
        pathname: `/address/${custPhone}`,
        search: createSearchParams({ rerouted: true }).toString(),
      });
    }
  }, [address, custPhone, navigate]);

  const renderContent = () => {
    if (!selectedTab) return null;

    const activeTab = tabs.find((tab) => tab.key === selectedTab);
    if (!activeTab) return null;

    const activeData = activeTab.source === 'menu' ? menuData[activeTab.type] : comboData;
    if (!activeData) return null;
    const SelectedComponent = Types[getComponentKey(activeTab.type)];
    return SelectedComponent ? <SelectedComponent type={activeTab.type} data={activeData} /> : null;
  };

  if (tabs.length) {
    return (
    <Container>
      <Div c="tab_wraper">
        {tabs.map((tab) => (
          <Div
            c="tab"
            open={selectedTab === tab.key}
            key={tab.key}
            onClick={() => setSelectedTab(tab.key)}
          >
            <img src={`/images/menu-${tab.type === 'CC (Pizza)' ? 'pizza' : tab?.type?.toLowerCase()}.png`} alt="pizza_menu" />
            <span>{tab.type === 'CC' ? 'Pizza (CC)' : tab.type}</span>
          </Div>
        ))}
      </Div>

      <Div c="tab_content">
        {renderContent()}
      </Div>
    </Container>
  );
  }

  return null;
};

export default Foodcart;

const Container = styled.div`
  .tab_wraper {
    display: flex;
    .tab {
      padding: 15px 20px;
      border: 1px solid rgba(0, 0, 0, 0.1);
      border-right: none;
      font-size: 14px;
      cursor: pointer;
      display:flex;
      align-items:center;
      gap:10px;
      img{
        width:30px;
        height:30px;
        object-fit:contain;
      }
      &:last-child {
        border: 1px solid rgba(0, 0, 0, 0.1);
      }
      &[open] {
        color: ${({ theme }) => theme.color};
        font-weight: bold;
        border-bottom: 1px solid white;
        cursor: default;
      }
    }
  }
  .tab_content {
    margin-top: -1px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding-top: 15px;
  }
`;
